import Grid from "@mui/material/Grid";
import Pagination from "@mui/material/Pagination";
import { styled, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Hit } from "_shared/_api/search/search-results";
import Guid from "_shared/guids/guid";
import _, { Dictionary } from "lodash";
import MarketplaceAPIService from "marketplace/_api/marketplace-api-service";
import MarketplaceSearchAPIRequest from "marketplace/_api/queries/marketplace-search-api-query";
import MarketplaceSearchInfoAPIResponse from "marketplace/_api/response-contracts/marketplace-search-info-api-response";
import ResultSkeleton from "marketplace/_views/result-skeleton";
import ResultsList from "marketplace/_views/results-list";
import IndividualAPIService from "marketplace/individuals/_api/individual-api-service";
import MarketplaceIndividualAPIResponse from "marketplace/individuals/_api/response-contracts/marketplace-individual-api-response";
import Individual from "marketplace/individuals/individual";
import MarketplaceIndividualInfoAPIResponse from "marketplace/individuals/profiles/_api/response-contracts/marketplace-individual-info-api-response";
import MarketplaceTeamInfoAPIResponse from "marketplace/team-profile/_api/response-contracts/marketplace-team-info-api-response";
import MarketplaceTeamAPIService from "marketplace/teams/_api/marketplace-team-api-service";
import MarketplaceTeamSearch from "marketplace/teams/_api/request-contracts/marketplace-team-search";
import Team from "marketplace/teams/team";
import { enqueueSnackbar } from "notistack";
import React, { ChangeEvent, useEffect } from "react";
import { NavigateFunction, SetURLSearchParams } from "react-router-dom";
import Session from "users/__session/session";

const ComponentContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("xl")]: {
    paddingRight: 0,
  },
  display: "flex",
}));
const ResultCount = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down("xl")]: {
    marginLeft: theme.spacing(2),
  },
}));
const LowerPagination = styled(Grid)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  marginBottom: theme.spacing(1),
}));
const UpperPagination = styled(Grid)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

type ResultsProps = {
  session: Readonly<Session>;
  searchTerm: string;
  category: string;
  filters: Dictionary<string>;
  sortBy: string;
  embedded?: boolean;
  selectingTeamLeader?: boolean;
  selectingTeamMember?: boolean;
  selectingTeam?: boolean;
  selectedTeamMemberUserIds?: Guid[];
  navigate?: NavigateFunction;
  searchParams?: URLSearchParams;
  setSearchParams?: SetURLSearchParams;
  onResults: (results: number | undefined) => void;
  onTeamMemberSelected?: (teamMember: Individual) => void;
  onTeamSelected?: (team: Team) => void;
};

export default function Results(props: Readonly<ResultsProps>) {
  const {
    session,
    searchTerm,
    category,
    filters,
    sortBy,
    embedded,
    selectingTeamLeader,
    selectingTeamMember,
    selectingTeam,
    selectedTeamMemberUserIds,
    searchParams,
    setSearchParams,
    navigate,
    onResults,
    onTeamMemberSelected,
    onTeamSelected,
  } = props;

  const [results, setResults] = React.useState<
    Hit<
      | MarketplaceSearchInfoAPIResponse
      | MarketplaceIndividualInfoAPIResponse
      | MarketplaceTeamInfoAPIResponse
      | MarketplaceIndividualAPIResponse
    >[]
  >([]);
  const [loading, setLoading] = React.useState(false);
  const [page, setPage] = React.useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(25);
  const [numResults, setNumResults] = React.useState<number>(0);
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"), {
    matchMedia: globalThis.matchMedia,
  });

  async function fetchResultList(
    searchTerm: string,
    filters: Dictionary<string> | undefined,
    abortController: AbortController
  ) {
    try {
      setLoading(true);
      let response;
      const request = new MarketplaceSearchAPIRequest(filters);
      const individualApiService = new IndividualAPIService(session);
      const teamApiService = new MarketplaceTeamAPIService({ session });

      if (selectingTeam) {
        let teamSearch = new MarketplaceTeamSearch({
          from: page && rowsPerPage ? (page - 1) * rowsPerPage : 0,
          size: rowsPerPage,
          query: searchTerm,
          contactPerson: filters?.contactPerson,
          description: filters?.description,
          name: filters?.name,
          team: filters?.team,
          searchParams: request.searchParams
        });
        response = await teamApiService.searchTeams(teamSearch);
      } else if (selectingTeamLeader) {
        response = await individualApiService.searchIndividuals(request, abortController, true);
      } else if (selectingTeamMember) {
        response = await individualApiService.searchIndividuals(request, abortController);
      } else {
        const marketplaceApiService = new MarketplaceAPIService(session);
        response = await marketplaceApiService.searchAll(request, abortController);
      }

      let hits = response.hits ? [...response.hits] : [];

      // Add self to the results list if the user is selecting a vendor-team leader
      if (
        selectingTeamLeader &&
        session.user?.individualId &&
        !response.hits?.some((hit) => hit.data.id === session.user?.individualId?.value)
      ) {
        const self = await individualApiService.getMarketplaceIndividualById(
          session.user.individualId,
          abortController
        );
        // NOTE: getMarketplaceIndividualById must return MarketplaceIndividualInfoAPIResponse because its "type" field is needed in the
        // <Results> component and "type" isn't defined by the IndividualSchema.
        // NOTE: This is the ONLY place where the IndividualAPIService.getMarketplaceIndividualById method is called.
        const selfResult: Hit<MarketplaceIndividualInfoAPIResponse> = { 
          data: self,
          score: 0, 
          highlight: {} 
        };
        if (self) {
          hits = [selfResult, ...hits];
        }
      }

      setResults(hits);
      setNumResults(hits.length ?? 0);
      setPage(response.from ?? 0);
      setRowsPerPage(response.size ?? 25);

      onResults(hits.length);
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Couldn't get results. Please try again", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    async function checkQueryParams() {
      let abortController = new AbortController();

      if (!searchParams?.has("query") && !searchParams?.has("id") && !searchParams?.has("type")) {
        await fetchResultList(searchTerm, filters, abortController);
        return;
      }

      const id = searchParams?.get("id") ?? "";
      const type = searchParams?.get("type") ?? "";

      if (id !== "" && type !== "") {
        navigate?.(`/marketplace/${type}/${id}`);
        return;
      }

      const term = searchParams?.get("query") ?? searchTerm ?? "";
      let queryFilters = _.fromPairs(Array.from(searchParams?.entries()));

      setPage(Number.parseInt(searchParams?.get("pageIndex") ?? "0"));
      await fetchResultList(term, queryFilters, abortController);

      return () => {
        abortController.abort();
        abortController = new AbortController();
      };
    }
    void checkQueryParams();
  }, [searchTerm, category, sortBy, searchParams]);

  async function handlePageChange(_event: ChangeEvent<unknown>, page: number) {
    setResults([]);
    setPage(page);

    const params = searchParams;

    if (params) {
      params.set("pageIndex", page.toString());
      setSearchParams?.(params);
    }

    let abortController = new AbortController();
    try {
      await fetchResultList(searchTerm, filters, abortController);
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Couldn't get results. Please try again", {
        variant: "error",
      });
    }
  }

  return (
    <ComponentContainer>
      <Grid container direction="column" size="grow">
        <Grid>
          {loading && <ResultSkeleton />}
          {!loading && (
            <UpperPagination container justifyContent="space-between" alignItems="center">
              <Grid>
                <ResultCount variant="h6">
                  {numResults} Result
                  {numResults < 1 || numResults > 1 ? "s" : ""}
                </ResultCount>
              </Grid>
              {numResults > rowsPerPage && (
                <Grid>
                  <Pagination
                    count={Math.ceil(numResults / rowsPerPage)}
                    page={page}
                    size={isMediumDisplaySize ? "medium" : "large"}
                    onChange={handlePageChange}
                  />
                </Grid>
              )}
            </UpperPagination>
          )}
        </Grid>
        {!loading && (
          <ResultsList
            results={results}
            embedded={embedded}
            selectingTeamLeader={selectingTeamLeader}
            selectingTeamMember={selectingTeamMember}
            selectingTeam={selectingTeam}
            selectedTeamMemberUserIds={selectedTeamMemberUserIds}
            navigate={navigate}
            onTeamMemberSelected={onTeamMemberSelected}
            onTeamSelected={onTeamSelected}
          />
        )}
        {numResults > rowsPerPage && !loading && (
          <LowerPagination size="grow">
            <Pagination
              count={Math.ceil(numResults / rowsPerPage)}
              page={page}
              disabled={Math.ceil(numResults / rowsPerPage) === 1}
              size={isMediumDisplaySize ? "medium" : "large"}
              onChange={handlePageChange}
            />
          </LowerPagination>
        )}
      </Grid>
    </ComponentContainer>
  );
}
